import { GidType, toGid } from "@redotech/redo-model/shopify/gid-utils";
import { ProductSortKeys } from "@redotech/shopify-client/storefront.graphql";
import { Gid } from "@redotech/shopify-util/id";
import { filterTruthy } from "@redotech/util/array";
import { chunks } from "@redotech/util/iterator";
import {
  GetBulkProductImagesQuery,
  GetBulkProductImagesQueryVariables,
  GetCollectionQuery,
  GetCollectionQueryVariables,
  GetCollectionsQuery,
  GetCollectionsQueryVariables,
  GetIdTitleProductsQuery,
  GetIdTitleProductsQueryVariables,
  GetProductImageQuery,
  GetProductImageQueryVariables,
  GetProductQuery,
  GetProductQueryVariables,
  GetProductsByGidsQuery,
  GetProductsByGidsQueryVariables,
  GetProductsMinimumQuery,
  GetProductsMinimumQueryVariables,
  GetProductsQuery,
  GetProductsQueryVariables,
  GetProductVariantDisplayableQuery,
  GetProductVariantDisplayableQueryVariables,
  GetProductVariantQuery,
  GetProductVariantQueryVariables,
  GetProductWithMetafieldQuery,
  GetProductWithMetafieldQueryVariables,
  GetProductWithMetaobjectQuery,
  GetProductWithMetaobjectQueryVariables,
  SearchProductsQuery,
  SearchProductsQueryVariables,
} from "./shopify-storefront.graphql";
import {
  handleErrors,
  ShopifyStorefrontClient,
  ShopifyStorefrontClientInterface,
} from "./storefront-client";

export const PRODUCT_INFO_FRAGMENT = /* GraphQL */ `
  fragment ProductInfo on Product {
    id
    title
    handle
    vendor
    onlineStoreUrl
    totalInventory
    description
    productType
    isGiftCard
    collections(first: 100) {
      nodes {
        id
        title
      }
    }
    availableForSale
    description
    productType
    tags
    publishedAt
    options {
      id
      name
      values
    }
    priceRange {
      minVariantPrice {
        amount
        currencyCode
      }
      maxVariantPrice {
        amount
        currencyCode
      }
    }
    featuredImage {
      altText
      url
    }
    variants(first: 200) {
      nodes {
        id
        requiresShipping
        currentlyNotInStock
        availableForSale
        weight
        weightUnit
        price {
          amount
          currencyCode
        }
        compareAtPrice {
          amount
          currencyCode
        }
        id
        sku
        barcode
        title
        selectedOptions {
          name
          value
        }
        quantityAvailable
        image {
          url
        }
        title
        backorderDate: metafield(namespace: "custom", key: "backorder_date") {
          value
        }
      }
    }
    images(first: 10) {
      nodes {
        altText
        id
        url
      }
    }
  }
`;

export const ID_TITLE_PRODUCTS_INFO_FRAGMENT = /* GraphQL */ `
  fragment IdTitleProductsInfo on Product {
    id
    title
  }
`;

const getIdTitleProductsQuery = /* GraphQL */ `
  ${ID_TITLE_PRODUCTS_INFO_FRAGMENT}
  query getIdTitleProducts($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        ...IdTitleProductsInfo
      }
    }
  }
`;
export async function getIdTitleProducts(
  client: ShopifyStorefrontClient,
  ids: string[],
): Promise<{ id: string; title: string }[]> {
  const MAX_BATCH_SIZE = 250;
  const results: { id: string; title: string }[] = [];

  // Process ids in batches of 250 as Shopify has a limit of 250 ids per request
  for (let i = 0; i < ids.length; i += MAX_BATCH_SIZE) {
    const batchIds = ids.slice(i, i + MAX_BATCH_SIZE);

    const { data, errors } = await client.request<
      GetIdTitleProductsQuery,
      GetIdTitleProductsQueryVariables
    >(getIdTitleProductsQuery, { ids: batchIds });

    handleErrors(errors);

    results.push(...filterTruthy(data?.nodes ?? []));
  }

  return results;
}

const getProductsQuery = /* GraphQL */ `
  ${PRODUCT_INFO_FRAGMENT}
  query getProducts(
    $after: String
    $first: Int
    $query: String
    $sortKey: ProductSortKeys = BEST_SELLING
  ) {
    products(after: $after, first: $first, query: $query, sortKey: $sortKey) {
      nodes {
        ...ProductInfo
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

const getProductQuery = /* GraphQL */ `
  ${PRODUCT_INFO_FRAGMENT}
  query getProduct($id: ID!) {
    product(id: $id) {
      ...ProductInfo
    }
  }
`;

const getProductWithMetafieldQuery = /* GraphQL */ `
  ${PRODUCT_INFO_FRAGMENT}
  query getProductWithMetafield($id: ID!, $key: String!, $namespace: String) {
    product(id: $id) {
      ...ProductInfo
      chosenMetafield: metafield(key: $key, namespace: $namespace) {
        value
        type
      }
    }
  }
`;

const getProductWithMetaobjectQuery = /* GraphQL */ `
  ${PRODUCT_INFO_FRAGMENT}
  query getProductWithMetaobject(
    $id: ID!
    $key: String!
    $namespace: String
    $metaobjectKey: String!
  ) {
    product(id: $id) {
      ...ProductInfo
      chosenMetafield: metafield(key: $key, namespace: $namespace) {
        value
        type
        reference {
          ... on Metaobject {
            field(key: $metaobjectKey) {
              value
              type
            }
          }
        }
      }
    }
  }
`;

const getProductVariantQuery = /* GraphQL */ `
  query getProductVariant($id: ID!) {
    node(id: $id) {
      ... on ProductVariant {
        id
        title
        price {
          amount
          currencyCode
        }
        product {
          id
          title
          handle
          vendor
        }
        quantityAvailable
        availableForSale
      }
    }
  }
`;

const getProductVariantDisplayableQuery = /* GraphQL */ `
  query getProductVariantDisplayable($id: ID!) {
    node(id: $id) {
      ... on ProductVariant {
        id
        title
        sku
        availableForSale
        compareAtPrice {
          amount
          currencyCode
        }
        price {
          amount
          currencyCode
        }
        unitPrice {
          amount
          currencyCode
        }
        image {
          id
          url
          altText
          width
          height
        }
        product {
          title
          handle
        }
        selectedOptions {
          name
          value
        }
      }
    }
  }
`;

const getProductsByGidsQuery = /* GraphQL */ `
  ${PRODUCT_INFO_FRAGMENT}
  query getProductsByGids($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        ...ProductInfo
      }
    }
  }
`;

export type ProductVariantInfo = GetProductVariantQuery["node"];
export type ProductVariantDisplayableInfo =
  GetProductVariantDisplayableQuery["node"];
export type ProductInfo = GetProductsQuery["products"]["nodes"][number];
export type ProductInfoWithMetafield = GetProductWithMetafieldQuery["product"];
export type ProductInfoWithMetaobject =
  GetProductWithMetaobjectQuery["product"];

export async function* getProducts(
  client: ShopifyStorefrontClient,
  query: Omit<GetProductsQueryVariables, "after">,
): AsyncIterable<ProductInfo> {
  let after: string | undefined = undefined;
  while (true) {
    const variables = { ...query, after, first: query.first ?? 10 };

    const { data, errors } = await client.request<
      GetProductsQuery,
      GetProductsQueryVariables
    >(getProductsQuery, variables);

    handleErrors(errors);

    if (!data) {
      return;
    }
    // if a store doesn't have products should we return an error?
    if (!data.products) {
      break;
    }
    for (const product of data.products.nodes) {
      yield product;
    }
    if (!data.products.pageInfo.hasNextPage) {
      break;
    }

    after = (data.products.pageInfo.endCursor ?? undefined) as
      | string
      | undefined;
  }
}

export async function getProductsArray({
  client,
  query,
  max,
}: {
  client: ShopifyStorefrontClient;
  query: Omit<GetProductsQueryVariables, "after" | "first">;
  max: number;
}): Promise<ProductInfo[]> {
  const iterator = getProducts(client, { ...query, first: max });
  const results: ProductInfo[] = [];
  try {
    for await (const product of iterator) {
      results.push(product);
      if (max && results.length >= max) {
        break;
      }
    }
  } catch (e) {
    console.error(e);
  }
  return results;
}

export const PRODUCT_INFO_FRAGMENT_MINIMUM = /* GraphQL */ `
  fragment ProductInfoMinimum on Product {
    id
    descriptionHtml
    featuredImage {
      url
    }
    variants(first: 1) {
      nodes {
        price {
          amount
        }
      }
    }
  }
`;

export type ProductInfoMinimum =
  GetProductsMinimumQuery["products"]["nodes"][number];

const getProductsMinimumQuery = /* GraphQL */ `
  ${PRODUCT_INFO_FRAGMENT_MINIMUM}
  query getProductsMinimum(
    $after: String
    $first: Int
    $query: String
    $sortKey: ProductSortKeys = BEST_SELLING
  ) {
    products(after: $after, first: $first, query: $query, sortKey: $sortKey) {
      nodes {
        ...ProductInfoMinimum
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

export async function* getProductsMinimum(
  client: ShopifyStorefrontClient,
  query: Omit<GetProductsMinimumQueryVariables, "after">,
): AsyncIterable<ProductInfoMinimum> {
  let after: string | undefined = undefined;
  while (true) {
    const variables = { ...query, after, first: query.first ?? 10 };
    console.log("variables", variables);
    const { data, errors } = await client.request<
      GetProductsMinimumQuery,
      GetProductsMinimumQueryVariables
    >(getProductsMinimumQuery, variables);

    handleErrors(errors);

    if (!data) {
      return;
    }
    // if a store doesn't have products should we return an error?
    if (!data.products) {
      break;
    }
    for (const product of data.products.nodes) {
      yield product;
    }
    if (!data.products.pageInfo.hasNextPage) {
      break;
    }

    after = (data.products.pageInfo.endCursor ?? undefined) as
      | string
      | undefined;
  }
}

export async function getProductsByGids(
  client: ShopifyStorefrontClient,
  ids: Gid[],
): Promise<ProductInfo[]> {
  const MAX_BATCH_SIZE = 250;
  const results: ProductInfo[] = [];

  // Process ids in batches of 250 as Shopify has a limit of 250 ids per request
  for (let i = 0; i < ids.length; i += MAX_BATCH_SIZE) {
    const batchIds = ids.slice(i, i + MAX_BATCH_SIZE);

    const { data, errors } = await client.request<
      GetProductsByGidsQuery,
      GetProductsByGidsQueryVariables
    >(getProductsByGidsQuery, { ids: batchIds });

    handleErrors(errors);

    results.push(...filterTruthy(data?.nodes ?? []));
  }

  return results;
}

export async function getVariant(
  client: ShopifyStorefrontClient,
  id: string,
): Promise<ProductVariantInfo | null> {
  const { data, errors } = await client.request<
    GetProductVariantQuery,
    GetProductVariantQueryVariables
  >(getProductVariantQuery, {
    id: id.includes("gid://") ? id : `gid://shopify/ProductVariant/${id}`,
  });

  handleErrors(errors);

  return data?.node ?? null;
}

export async function getDisplayableVariant(
  client: ShopifyStorefrontClient,
  id: string,
): Promise<ProductVariantDisplayableInfo | null> {
  const { data, errors } = await client.request<
    GetProductVariantDisplayableQuery,
    GetProductVariantDisplayableQueryVariables
  >(getProductVariantDisplayableQuery, {
    id: id.includes("gid://") ? id : `gid://shopify/ProductVariant/${id}`,
  });

  handleErrors(errors);

  return data?.node ?? null;
}

export async function getProduct(
  client: ShopifyStorefrontClient,
  id: string,
): Promise<ProductInfo | null> {
  const { data, errors } = await client.request<
    GetProductQuery,
    GetProductQueryVariables
  >(getProductQuery, {
    id: id.includes("gid://") ? id : `gid://shopify/Product/${id}`,
  });

  handleErrors(errors);

  return data?.product ?? null;
}

export async function getProductWithMetafield(
  client: ShopifyStorefrontClient,
  id: string,
  metafieldKey: string,
  metafieldNamespace?: string,
  metaobjectKey?: string,
): Promise<ProductInfoWithMetafield | ProductInfoWithMetaobject | null> {
  const payload = {
    id: id.includes("gid://") ? id : `gid://shopify/Product/${id}`,
    key: metafieldKey,
    namespace: metafieldNamespace,
  };

  const { data, errors } = await (metaobjectKey
    ? client.request<
        GetProductWithMetaobjectQuery,
        GetProductWithMetaobjectQueryVariables
      >(getProductWithMetaobjectQuery, { ...payload, metaobjectKey })
    : client.request<
        GetProductWithMetafieldQuery,
        GetProductWithMetafieldQueryVariables
      >(getProductWithMetafieldQuery, payload));

  handleErrors(errors);

  return data?.product ?? null;
}

export const getProductsByCollection = async (
  client: ShopifyStorefrontClient,
  collectionId: Gid,
  count: number,
): Promise<ProductInfo[]> => {
  const collection = await getCollection(client, collectionId);
  if (!collection) {
    throw new Error("Collection not found");
  }

  const items = collection.products.nodes.slice(0, count);
  return items;
};

export const getProductsByTags = async (
  client: ShopifyStorefrontClient,
  tags: string[],
  amount: number = 250,
  sortKey: ProductSortKeys = ProductSortKeys.BestSelling,
): Promise<ProductInfo[]> => {
  const products: ProductInfo[] = [];
  try {
    const iterator = getProducts(client, {
      first: amount,
      query: `tag:${tags.map((tag: string) => `"${tag}"`).join(" OR ")}`,
      sortKey,
    });
    for await (const product of iterator) {
      products.push(product);
      if (products.length >= amount) {
        break;
      }
    }
  } catch (e) {
    console.error("Error getting top products", e);
  }

  return products;
};

export async function getRemainingInventory(
  client: ShopifyStorefrontClient,
  id: string,
): Promise<number | null> {
  const { data, errors } = await client.request<
    GetProductQuery,
    GetProductQueryVariables
  >(getProductQuery, {
    id: id.includes("gid://") ? id : `gid://shopify/Product/${id}`,
  });

  handleErrors(errors);

  const product = data?.product;
  if (!product) {
    return null;
  }

  let remainingInventory = 0;
  for (const variant of product.variants.nodes) {
    remainingInventory += variant.quantityAvailable ?? 0;
  }

  return remainingInventory;
}

const getCollectionQuery = /* GraphQL */ `
  ${PRODUCT_INFO_FRAGMENT}
  query getCollection($id: ID!) {
    collection(id: $id) {
      id
      title
      products(first: 100) {
        nodes {
          ...ProductInfo
        }
      }
    }
  }
`;

export type CollectionProduct = GetCollectionQuery["collection"] | null;
export type CollectionProductInfo = NonNullable<
  GetCollectionQuery["collection"]
>["products"]["nodes"][number];
export type CollectionProductOptions = NonNullable<
  GetCollectionQuery["collection"]
>["products"]["nodes"][number]["options"];

export async function getCollection(
  client: ShopifyStorefrontClient,
  id: string,
): Promise<CollectionProduct | null> {
  const { data, errors } = await client.request<
    GetCollectionQuery,
    GetCollectionQueryVariables
  >(getCollectionQuery, {
    id: id.includes("gid://") ? id : `gid://shopify/Collection/${id}`,
  });

  handleErrors(errors);

  return data?.collection ?? null;
}

const getCollectionsQuery = /* GraphQL */ `
  ${PRODUCT_INFO_FRAGMENT}
  query getCollections($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Collection {
        id
        title
        products(first: 100) {
          nodes {
            ...ProductInfo
          }
        }
      }
    }
  }
`;

export async function getCollections(
  client: ShopifyStorefrontClient,
  ids: Gid[],
): Promise<CollectionProduct[]> {
  const { data, errors } = await client.request<
    GetCollectionsQuery,
    GetCollectionsQueryVariables
  >(getCollectionsQuery, { ids });

  handleErrors(errors);

  return data?.nodes ?? [];
}

const searchProductQuery = /* GraphQL */ `
  ${PRODUCT_INFO_FRAGMENT}
  query searchProducts($query: String!, $first: Int) {
    search(query: $query, first: $first, types: PRODUCT) {
      edges {
        node {
          ...ProductInfo
        }
      }
    }
  }
`;

export async function searchProducts(
  client: ShopifyStorefrontClientInterface,
  query: string,
  numResults: number = 100,
): Promise<ProductInfo[]> {
  const { data, errors } = await client.request<
    SearchProductsQuery,
    SearchProductsQueryVariables
  >(searchProductQuery, { query, first: numResults });

  handleErrors(errors);

  return data?.search.edges.map((edge) => edge.node) ?? [];
}

const searchProductWithContextQuery = /* GraphQL */ `
  ${PRODUCT_INFO_FRAGMENT}
  query searchProductsWithContext(
    $query: String!
    $first: Int
    $country: CountryCode!
  ) @inContext(country: $country) {
    search(query: $query, first: $first, types: PRODUCT) {
      edges {
        node {
          ...ProductInfo
        }
      }
    }
  }
`;

export async function searchProductsWithContext({
  client,
  query,
  limit,
  countryCode,
}: {
  client: ShopifyStorefrontClient;
  query: string;
  limit: number;
  countryCode?: string;
}): Promise<ProductInfo[]> {
  if (!countryCode) {
    return searchProducts(client, query, limit);
  }

  const { data, errors } = await client.request<any, any>(
    searchProductWithContextQuery,
    { query, first: limit, country: countryCode },
  );

  handleErrors(errors);

  return data?.search.edges.map((edge: any) => edge.node) ?? [];
}

const PRODUCT_IMAGE_FRAGMENT = /* GraphQL */ `
  fragment ProductImage on Product {
    featuredImage {
      url(transform: { maxWidth: 80, maxHeight: 80, crop: CENTER, scale: 1 })
    }
  }
`;

const getProductImageQuery = /* GraphQL */ `
  ${PRODUCT_IMAGE_FRAGMENT}
  query getProductImage($id: ID!) {
    product(id: $id) {
      ...ProductImage
    }
  }
`;

const getBulkProductImagesQuery = /* GraphQL */ `
  ${PRODUCT_IMAGE_FRAGMENT}
  query getBulkProductImages($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        ...ProductImage
        id
      }
    }
  }
`;

export async function getProductImage(
  client: ShopifyStorefrontClient,
  id: string,
): Promise<string | null> {
  const formattedId = toGid(GidType.PRODUCT, id);
  const { data, errors } = await client.request<
    GetProductImageQuery,
    GetProductImageQueryVariables
  >(getProductImageQuery, { id: formattedId });

  handleErrors(errors);

  return data?.product?.featuredImage?.url ?? null;
}

/**
 * Bulk fetch product images for multiple products using Shopify's nodes query.
 * This function eliminates N+1 query issues by fetching all product images in batched requests.
 *
 * @param client - The Shopify Storefront API client
 * @param productGids - Array of product GIDs for which to fetch images
 * @returns Map of product GID to image URL (or null if no image exists)
 */
export async function getBulkProductImages(
  client: ShopifyStorefrontClient,
  productGids: string[],
): Promise<Map<string, string | null>> {
  if (productGids.length === 0) {
    return new Map();
  }

  const MAX_BATCH_SIZE = 250;
  const imageMap = new Map<string, string | null>();

  // Process productGids in batches of 250 as Shopify has a limit of 250 ids per request
  for (const batch of chunks(productGids, MAX_BATCH_SIZE)) {
    const batchIds = batch.map((gid) => toGid(GidType.PRODUCT, gid));

    try {
      const { data, errors } = await client.request<
        GetBulkProductImagesQuery,
        GetBulkProductImagesQueryVariables
      >(getBulkProductImagesQuery, { ids: batchIds });

      handleErrors(errors);

      // Map the results by product GID
      if (data?.nodes) {
        for (const node of data.nodes) {
          if (node) {
            imageMap.set(node.id, node.featuredImage?.url ?? null);
          }
        }
      }

      // Ensure all requested products have an entry, even if null
      for (const gid of batchIds) {
        if (!imageMap.has(gid)) {
          imageMap.set(gid, null);
        }
      }
    } catch (error) {
      // If batch fails, set all products in this batch to null
      for (const gid of batchIds) {
        imageMap.set(gid, null);
      }
    }
  }

  return imageMap;
}

export async function getVariantInventory(
  client: ShopifyStorefrontClient,
  variantId: string,
): Promise<number | null> {
  const { data, errors } = await client.request<
    GetProductVariantQuery,
    GetProductVariantQueryVariables
  >(getProductVariantQuery, { id: toGid(GidType.PRODUCT_VARIANT, variantId) });

  handleErrors(errors);

  const variant = data?.node as ProductVariantInfo;
  if (!variant) {
    return null;
  }

  return variant.quantityAvailable ?? null;
}
